import { Injectable, inject } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import { BehaviorSubject, of } from 'rxjs';
import { CommonApiFuncService, StorageService, StorageType } from '@anddone/ui-utilities';
import { APIConstants } from '../constants/api.constant';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DeveloperService {
  loggedInUserData: any;
  public webhookDetailUpdate = new BehaviorSubject(false);
  currentWebhookDetailUpdate = this.webhookDetailUpdate.asObservable();
  private selectedWebhook = new BehaviorSubject<boolean>(false);
  private commonAPIFuncService = inject(CommonApiFuncService);
  private storageService = inject(StorageService);

  getLoggedInData() {
    return JSON.parse(this.storageService.get(StorageType.Session, 'userDetails') ?? '{}');
  }

  getAllWebhook(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    let url = APIConstants.developer.getAllWebhoooks.replace('{allyId}', this.loggedInUserData.id);
    url = `${url}${this.commonAPIFuncService.buildQuery(reqObj)}`;
    const options = {
      customBaseUrl: environment.mastersBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`getAllWebhook success`)),
      catchError(this.commonAPIFuncService.handleError('getAllWebhook failed'))
    );
  }

  getAllWebhookEvents() {
    this.loggedInUserData = this.getLoggedInData();
    if (this.loggedInUserData) {
      const url = APIConstants.developer.getAllWebhookEvents.replace(
        '{allyId}',
        this.loggedInUserData.id
      );
      const options = {
        customBaseUrl: environment.mastersBaseUrl,
      };
      return this.commonAPIFuncService.get(url, options)?.pipe(
        tap((_a) => this.commonAPIFuncService.log(`getAllWebhookEvents success`)),
        catchError(this.commonAPIFuncService.handleError('getAllWebhookEvents failed'))
      );
    }
    return of(null);
  }

  addWebhook(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const url = APIConstants.developer.addWebhook.replace('{allyId}', this.loggedInUserData?.id);
    const options = {
      customBaseUrl: environment.mastersBaseUrl,
    };
    return this.commonAPIFuncService.post(url, reqObj.data, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`addWebhook success`)),
      catchError(this.commonAPIFuncService.handleError('addWebhook failed'))
    );
  }

  updateWebhook(reqObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const url = APIConstants.developer.updateWebhook
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{id}', reqObj.id);
    const options = {
      customBaseUrl: environment.mastersBaseUrl,
      customHeaders: {
        'X-Version': '2.1',
      },
    };
    return this.commonAPIFuncService.put(url, reqObj.data, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`updateWebhook success`)),
      catchError(this.commonAPIFuncService.handleError('updateWebhook failed'))
    );
  }

  deleteWebhook(id: string) {
    this.loggedInUserData = this.getLoggedInData();
    const url = APIConstants.developer.deleteWebhook
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.mastersBaseUrl,
    };
    return this.commonAPIFuncService.delete(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`deleteWebhook success`)),
      catchError(this.commonAPIFuncService.handleError('deleteWebhook failed'))
    );
  }

  rollApiKey(obj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const url = APIConstants.developer.publicApiKey
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{keyType}', obj.keyType);
    const options = {
      customBaseUrl: environment.infraBaseUrl,
    };
    return this.commonAPIFuncService.post(url, obj, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`rollApiKey success`)),
      catchError(this.commonAPIFuncService.handleError('rollApiKey failed'))
    );
  }

  getAllWebhookEventLog(data: any) {
    this.loggedInUserData = this.getLoggedInData();
    if (this.loggedInUserData) {
      let url = APIConstants.developer.getAllWebhookEventsLogs.replace(
        '{allyId}',
        this.loggedInUserData.id
      );
      url = `${url}${this.commonAPIFuncService.buildQuery(data)}`;
      const options = {
        customBaseUrl: environment.mastersBaseUrl,
      };
      return this.commonAPIFuncService.get(url, options)?.pipe(
        tap((_a) => this.commonAPIFuncService.log(`getAllWebhookEventLog success`)),
        catchError(this.commonAPIFuncService.handleError('getAllWebhookEventLog failed'))
      );
    }
    return of(null);
  }

  retriggerWebhookEventLogAction(retriggerObj: any) {
    this.loggedInUserData = this.getLoggedInData();
    const url = APIConstants.developer.postFailedAllWebhookEvents.replace(
      '{allyId}',
      this.loggedInUserData?.id
    );
    const options = {
      customBaseUrl: environment.mastersBaseUrl,
    };
    return this.commonAPIFuncService.post(url, retriggerObj, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`retriggerWebhookEventLogAction success`)),
      catchError(this.commonAPIFuncService.handleError('retriggerWebhookEventLogAction failed'))
    );
  }

  getModuleListForAlly() {
    this.loggedInUserData = this.getLoggedInData();
    if (this.loggedInUserData) {
      const url = APIConstants.developer.getModuleListForAlly.replace(
        '{allyId}',
        this.loggedInUserData.id
      );
      const options = {
        customBaseUrl: environment.mastersBaseUrl,
      };
      return this.commonAPIFuncService.get(url, options)?.pipe(
        tap((_a) => this.commonAPIFuncService.log(`getModuleList success`)),
        catchError(this.commonAPIFuncService.handleError('getModuleList failed'))
      );
    }
    return of(null);
  }

  viewWebhookEventLogAction(id: any) {
    this.loggedInUserData = this.getLoggedInData();
    const url = APIConstants.developer.webhookeventlogsDetails
      .replace('{allyId}', this.loggedInUserData?.id)
      .replace('{id}', id);
    const options = {
      customBaseUrl: environment.mastersBaseUrl,
    };
    return this.commonAPIFuncService.get(url, options).pipe(
      tap((_a) => this.commonAPIFuncService.log(`viewWebhookEventLogAction success`)),
      catchError(this.commonAPIFuncService.handleError('viewWebhookEventLogAction failed'))
    );
  }

  // Getter for the key property
  getSelectedWebhook() {
    return this.selectedWebhook.asObservable(); // Expose as observable
  }

  // Setter for the key property
  setSelectedWebhook(value: any) {
    this.selectedWebhook.next(value); // Update the loader state
  }
}
